// =============================================================================
// SignedInPill — small "who am I" indicator in the top bar of each
// Platform layout. Reads role state from AuthContext (isSuperAdmin +
// myTenants) and renders the right label so an admin can never get
// confused about which context they're operating in.
//
// Three visual states:
//   • Super admin                  → ShieldCheck + "you@dg.com · Super admin"
//   • Tenant admin (current tenant from URL when available)
//                                  → Building2 + "you@abc.com · Admin of ABC-Travels"
//     - Falls back to myTenants[0] when not on /admin/tenants/:id/* path
//     - Shows "+N more" suffix when admin of multiple tenants
//   • Signed-in but neither        → User + "you@x.com" (rare — no-access state)
//
// Truncates email on small screens so the pill doesn't blow up the
// header. Uses theme tokens (bg-surface-100 / text-surface-700) so it
// inherits the existing palette without hard-coding light/dark.
// =============================================================================

import { useParams } from 'react-router-dom';
import { ShieldCheck, Building2, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function SignedInPill() {
  const { user, isSuperAdmin, myTenants } = useAuth();
  // useParams is safe to call outside a parameterised route — returns
  // {} when no `:tenantId` segment matches, which is exactly the
  // behaviour we want for super-admin pages.
  const { tenantId } = useParams();

  if (!user) return null;

  // ── Display name: profile full_name if available, fall back to
  // local-part-of-email, then email-whole. The auth user object on the
  // Platform side doesn't always carry full_name (depends on OAuth
  // provider), so email is the reliable default.
  const email      = user.email ?? '';
  const localPart  = email.split('@')[0] || 'User';
  const displayName = (user.user_metadata as { full_name?: string } | undefined)?.full_name?.trim()
                   || localPart;

  // ── Role + context resolution
  let icon = User;
  let roleLabel = '';
  let iconTint = 'text-surface-500';

  if (isSuperAdmin) {
    icon       = ShieldCheck;
    iconTint   = 'text-brand-600';
    roleLabel  = 'Super admin';
  } else if (myTenants.length > 0) {
    // Prefer the tenant in the URL when on /admin/tenants/:tenantId/*.
    // This matters when a single user is admin of multiple tenants:
    // the pill should show the one they're CURRENTLY managing.
    const currentEntry = (tenantId && myTenants.find(t => t.tenant.id === tenantId))
                       || myTenants[0];
    const tenantName = currentEntry.tenant.name;
    const extra = myTenants.length > 1 ? ` (+${myTenants.length - 1} more)` : '';
    icon      = Building2;
    iconTint  = 'text-brand-600';
    roleLabel = `Admin of ${tenantName}${extra}`;
  }

  const Icon = icon;

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-100 border border-overlay/[0.06] text-xs text-surface-700"
      title={email + (roleLabel ? ` — ${roleLabel}` : '')}
    >
      <Icon size={14} className={iconTint} />
      <span className="font-medium text-surface-900 truncate max-w-[160px]">{displayName}</span>
      {roleLabel && (
        <>
          <span className="text-surface-400">·</span>
          <span className="hidden sm:inline truncate max-w-[180px]">{roleLabel}</span>
        </>
      )}
    </div>
  );
}
